import React, {useState, useEffect} from 'react';
import {connect, useDispatch} from 'react-redux';
import {Transition} from '@headlessui/react';
import {X, ChevronDown, ChevronUp, Check} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {useFlowsLogic} from 'routes/FraudDetection/Flows/hooks/useFlows';
import {useQuery} from 'react-query';
import {fetchCompany} from '../../requests/queries/flows';
import {UPDATE_COMPANY_INFO} from '../../store/types';

const GettingStartedWidget = ({isOpen, onClose, auth: {userDetails}}) => {
  const navigate = useNavigate();
  const {flows} = useFlowsLogic({enabled: true});
  const dispatch = useDispatch();

  const latestFlowId = flows?.flows?.[0]?.id;
  const setupGuide = userDetails?.company?.setup_guide;
  const isActive = userDetails?.company?.billing_status;

  const isStep1Completed = setupGuide?.step1 ?? false;
  const isStep2Completed = setupGuide?.step2 ?? false;
  const isStep3Completed = setupGuide?.step3 ?? false;

  const isEnabledAndCanRetry =
    !!isActive && (!isStep1Completed || !isStep2Completed || !isStep3Completed);

  const {isLoading, data} = useQuery(
    ['pr-company-details'],
    () => fetchCompany(),
    {
      enabled: isEnabledAndCanRetry,
      retry: false,
      meta: {preventGlobalError: true},
    },
  );

  useEffect(() => {
    if (!isLoading && data?.entity) {
      const {name, ...rest} = data?.entity || {};
      dispatch({
        payload: rest,
        type: UPDATE_COMPANY_INFO,
      });
    }
  }, [data?.entity, dispatch, isLoading]);

  const steps = [
    {
      id: 1,
      title: 'Create a Flow',
      description:
        'Select a pre-built template or create a new flow from scratch',
      completed: isStep1Completed,
      path: '/flows',
      disabled: isStep1Completed,
    },
    {
      id: 2,
      title: 'Setup your Rules',
      description:
        'Add specific conditions to tell the system exactly what to flag',
      completed: isStep2Completed,
      path: latestFlowId ? `/flows/edit-process/${latestFlowId}` : '/flows',
      disabled: !isStep1Completed || isStep2Completed,
    },
    {
      id: 3,
      title: 'Send your first event',
      description: (
        <>
          Send your first event to see your new rules in action.{' '}
          <a
            href="https://api-docs.dojah.io/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="text-brandBlue underline decoration-1 italic underline-offset-2 hover:text-blue-700 cursor-pointer"
          >
            Visit Docs
          </a>
        </>
      ),
      completed: isStep3Completed,
      disabled: !isStep2Completed || isStep3Completed,
    },
  ];

  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('profiled_risk_widget_expanded');
      return saved !== null ? saved === 'true' : true;
    }
    return true;
  });

  useEffect(() => {
    localStorage.setItem('profiled_risk_widget_expanded', isExpanded);
  }, [isExpanded]);

  const totalSteps = steps.length;
  const completedSteps = steps.filter(s => s.completed).length;
  const progressPercentage = (completedSteps / totalSteps) * 100;

  if (!isOpen || !isActive) return null;

  return (
    <div className="fixed bottom-0 md:bottom-6 right-0 md:right-6 z-[60] w-full max-w-[528px] font-sans">
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] overflow-hidden">
        <div className="px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <h3 className="text-base font-medium text-[#080917]">
                Getting Started
              </h3>

              <div className="flex items-center gap-3 flex-1 max-w-[180px]">
                <div className="h-[10px] w-full bg-[#F9F9F9] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brandBlue rounded-full transition-all duration-500"
                    style={{width: `${progressPercentage}%`}}
                  />
                </div>
                <span className="text-tiny font-semibold text-[#080917]">
                  {completedSteps}/{totalSteps}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 rounded-full hover:bg-gray-50 text-gray-500 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronUp className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-50 text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <Transition
            show={!isExpanded}
            enter="transition-opacity duration-200 delay-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="hidden"
          >
            <p className="text-tiny text-[#667185] mt-1">
              {completedSteps} of {totalSteps} tasks completed.{` `}
              <a
                href="mailto:sales@profiledrisk.com"
                className="text-sm text-brandBlue italic font-medium hover:underline"
              >
                Contact Support for help
              </a>
            </p>
          </Transition>

          <Transition
            show={isExpanded}
            enter="transition-[max-height] duration-300 ease-in-out overflow-hidden"
            enterFrom="max-h-0 opacity-0"
            enterTo="max-h-[600px] opacity-100"
            leave="transition-[max-height] duration-200 ease-in-out overflow-hidden"
            leaveFrom="max-h-[600px] opacity-100"
            leaveTo="max-h-0 opacity-0"
          >
            <div className="pt-1">
              <p className="text-tiny text-[#667185] mb-4">
                Let&apos;s set up your fraud defense system in 3 quick steps.
              </p>

              <div className="space-y-3">
                {steps.map(step => {
                  const isActive = !step.disabled;
                  const isCompleted = step.completed;
                  const isLocked = step.disabled && !step.completed;
                  const hasAction = !!step.path || !!step.externalLink;

                  return (
                    <div
                      key={step.id}
                      onClick={() => {
                        if (step.disabled) return;

                        if (step.externalLink) {
                          window.open(
                            step.externalLink,
                            '_blank',
                            'noopener,noreferrer',
                          );
                        } else if (step.path) {
                          navigate(step.path);
                        }
                      }}
                      className={`
                        group relative flex items-center p-4 rounded-xl border transition-all
                        ${
                          isActive
                            ? `bg-[#F9F9F9] border-transparent shadow-sm ${
                                hasAction
                                  ? 'hover:bg-grey-50 cursor-pointer'
                                  : 'cursor-default'
                              }`
                            : isCompleted
                            ? 'bg-white border border-brandBlue cursor-default'
                            : 'bg-[#F9F9F9] border border-transparent cursor-not-allowed'
                        }
                      `}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {isCompleted ? (
                          <div className="w-5 h-5 rounded-full border-2 border-brandBlue flex items-center justify-center shadow-sm">
                            <Check className="w-3.5 h-3.5 text-brandBlue stroke-[3]" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-[#667185]" />
                        )}
                      </div>
                      <div className="ml-3.5">
                        <h4
                          className={`font-medium text-sm ${
                            isLocked ? 'text-gray-400' : 'text-[#080917]'
                          }`}
                        >
                          {step.title}
                        </h4>
                        <div
                          className={`text-[12px] mt-1 ${
                            isLocked ? 'text-gray-400' : 'text-[#667185]'
                          }`}
                        >
                          {step.description}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 mb-1">
                <a
                  href="mailto:sales@profiledrisk.com"
                  className="text-sm text-brandBlue italic font-medium hover:underline"
                >
                  Contact Support for help
                </a>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  );
};

export default connect(state => state)(GettingStartedWidget);
